// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { RelativeDateShortcut } from '@/constants/date-shortcut';
import { Link, Stack, Tooltip } from '@mui/material';
import { DateTime } from 'luxon';

interface Props {
  /**
   * The shortcuts to render as one-click buttons.
   */
  shortcuts: RelativeDateShortcut[];

  /**
   * Returns the anchor date that shortcut offsets are applied to. Evaluated
   * at click time so a dialog left open across midnight stays correct.
   */
  getAnchor: () => DateTime;

  /**
   * Human-readable name of the anchor date used in button tooltips
   * (e.g. "today" or "the promise ship date").
   */
  anchorName: string;

  /**
   * Called with the computed date when a shortcut is clicked.
   */
  onSelect: (date: DateTime) => void;
}

function shortcutTooltip(shortcut: RelativeDateShortcut, anchorName: string) {
  if (shortcut.days === 0) {
    return `Set to ${anchorName}`;
  }
  const dayLabel = shortcut.days === 1 ? 'day' : 'days';
  return `Set to ${shortcut.days} ${dayLabel} after ${anchorName}`;
}

/**
 * A row of subtle one-click date shortcut buttons intended to sit directly
 * under a date input, letting users pick common dates without opening the
 * date picker.
 */
export function DateShortcutButtons(props: Props) {
  return (
    <Stack direction="row" spacing={1.25} sx={{ mt: 0.5, ml: 1.75 }}>
      {props.shortcuts.map((shortcut) => (
        <Tooltip
          key={shortcut.label}
          arrow
          title={shortcutTooltip(shortcut, props.anchorName)}
        >
          <Link
            component="button"
            type="button"
            variant="caption"
            underline="hover"
            color="text.secondary"
            sx={{ '&:hover': { color: 'primary.main' } }}
            onClick={() => {
              props.onSelect(
                props.getAnchor().startOf('day').plus({ days: shortcut.days })
              );
            }}
          >
            {shortcut.label}
          </Link>
        </Tooltip>
      ))}
    </Stack>
  );
}
